/**
 * 62.48: the machinery under the bot parameter catalogue (src/ai/botParams.ts) — rows flattened from a clock's live
 * defaults (so the catalogue cannot drift from the code), a range for each, and path reads / writes. Pure; no knobs here.
 */
import type { BotParamDef, BotParamSet, BotParamValue } from '../types';

/** A range rule for one path: exact bounds, or `integer` alone (bounds still from the default). */
export interface RangeRule { min?: number; max?: number; integer?: boolean }

/** Key names that read as a 0–1 share, chance or threshold on a 0–1 trait. */
const UNIT_KEY = /(Fraction|Confidence|Factor|Dot|Skill|Aggression|Mobility|Risk|riskTolerance|aggression|mobility|chance|Chance|^minConfidence$)$/;
/** Key names that read as a count (whole numbers). */
const COUNT_KEY = /(Count|Steps|Candidates|Hops|Seeds|TopN|GridSize|Passes|Attempts|Turns|Rounds|Defers|Alternatives|Finalists|Cache|Ticks|Period|^maxSteps$|Lengths$)$/;

/** The default range for a number: [0, 1] for a share, a whole count from 0 to 4× (at least 4), else 0 to 8× (at least 1) — or the mirror for a negative. */
export function defaultRange(key: string, value: number): RangeRule {
  if (!Number.isFinite(value)) return { min: 0, max: Infinity };   // "no cap" (Infinity): any cap from 0 up
  if (COUNT_KEY.test(key) && Number.isInteger(value) && value >= 0) return { min: 0, max: Math.max(4, 4 * value), integer: true };
  if (UNIT_KEY.test(key) && value >= 0 && value <= 1) return { min: 0, max: 1 };
  if (value < 0) return { min: Math.min(-1, 8 * value), max: 0 };
  return { min: 0, max: Math.max(1, 8 * value) };
}

/**
 * Catalogue rows for every number and switch under `tree` (objects and arrays walked; strings and other leaves are not
 * knobs). `readerFor(path)` names the reading code; `rules` pins a range by exact path, or by any path ending `.<suffix>`.
 */
export function catalogueOf(prefix: string, tree: unknown, readerFor: (path: string) => string, rules: Record<string, RangeRule> = {}): BotParamDef[] {
  const out: BotParamDef[] = [];
  const walk = (node: unknown, path: string, key: string) => {
    if (typeof node === 'boolean') { out.push({ path, default: node, reader: readerFor(path) }); return; }
    if (typeof node === 'number') {
      const rule = rules[path] ?? Object.entries(rules).find(([k]) => k.startsWith('.') && path.endsWith(k))?.[1];
      const base = defaultRange(key, node);
      const r = { ...base, ...(rule ?? {}) };
      out.push({ path, default: node, min: r.min, max: r.max, ...(r.integer ? { integer: true } : {}), reader: readerFor(path) });
      return;
    }
    if (Array.isArray(node)) { node.forEach((v, i) => walk(v, `${path}.${i}`, key)); return; }
    if (node && typeof node === 'object') for (const [k, v] of Object.entries(node)) walk(v, `${path}.${k}`, k);
  };
  if (tree && typeof tree === 'object') for (const [k, v] of Object.entries(tree)) walk(v, `${prefix}.${k}`, k);
  return out;
}

/** Why a value is not allowed for its row, or null. */
export function paramProblem(def: BotParamDef | undefined, path: string, value: unknown): string | null {
  if (!def) return `unknown bot parameter '${path}'`;
  if (typeof def.default === 'boolean') return typeof value === 'boolean' ? null : `'${path}' is a switch (true / false), not ${JSON.stringify(value)}`;
  if (typeof value !== 'number' || Number.isNaN(value) || (!Number.isFinite(value) && def.max !== Infinity)) return `'${path}' is a number, not ${JSON.stringify(value)}`;
  if (def.integer && !Number.isInteger(value)) return `'${path}' is a whole number, not ${value}`;
  if ((def.min !== undefined && value < def.min) || (def.max !== undefined && value > def.max)) return `'${path}' = ${value} is outside its range [${def.min}, ${def.max}]`;
  return null;
}

/** A copy of `tree` with the leaf at `parts` set (only the objects along the path are copied). */
export function withPath<T>(tree: T, parts: readonly string[], value: BotParamValue): T {
  if (!parts.length) return value as unknown as T;
  const [head, ...rest] = parts;
  if (Array.isArray(tree)) {
    const copy = tree.slice();
    copy[Number(head)] = withPath(copy[Number(head)], rest, value);
    return copy as unknown as T;
  }
  const node = (tree ?? {}) as Record<string, unknown>;
  return { ...node, [head]: withPath(node[head], rest, value) } as T;
}

/** The value at `parts` under `tree`, or undefined. */
export function atPath(tree: unknown, parts: readonly string[]): unknown {
  let node = tree;
  for (const p of parts) {
    if (node === null || typeof node !== 'object') return undefined;
    node = (node as Record<string, unknown>)[p];
  }
  return node;
}

/** The entries of `values` under `prefix.` (the prefix stripped, split into parts), in path order — deterministic. */
export function entriesUnder(values: BotParamSet | undefined, prefix: string): Array<[string[], BotParamValue]> {
  if (!values) return [];
  return Object.keys(values).filter(k => k.startsWith(`${prefix}.`)).sort()
    .map(k => [k.slice(prefix.length + 1).split('.'), values[k]] as [string[], BotParamValue]);
}
